import {
  BookOpenText,
  Briefcase,
  ClockCounterClockwise,
  FunnelSimple,
  GraduationCap,
  IdentificationCard,
  ListStar,
  MapPin,
  Tag as TagIcon,
  Translate,
  UserList,
} from "@phosphor-icons/react";
import { useMemo } from "react";

import {
  FavoriteLead,
  LeadSource,
  LinkedInAccountsTag,
} from "features/leads/components";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import {
  ColumnsVisibilityFilter,
  DataTable,
} from "common/components/DataTable";
import useDataTable from "common/components/DataTable/useDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import type { Lead } from "common/types";

import { MANAGE_LEADS_ACTIONS } from "../../../constants";
import LeadNotesIndicator from "../LeadNotesIndicator";
import LeadCompanyTimeTag from "../LeadTags/LeadCompanyTimeTag";
import LeadDetailTag from "../LeadTags/LeadDetailTag";
import AddNotes from "../ManageLeads/AddNotes";
import FavoriteLeads from "../ManageLeads/FavoriteLeads";
import UpdateLabels from "../ManageLeads/UpdateLabels";
import DesktopLabels from "./DesktopLabels";

import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

interface DesktopTableProps {
  leads: Lead[];
  selectedLeads: Lead[];
  setSelectedLeads: (selectedLeads: Lead[]) => void;
  openSidebar: (leadId: string, action: "preview" | "notes") => void;
  isLoading: boolean;
  pageSize: number;
  totalItemCount: number;
  filtersComponent: ReactNode;
  emptyStateComponent: ReactNode;
}

export default function DesktopTable({
  leads,
  selectedLeads,
  setSelectedLeads,
  openSidebar,
  isLoading,
  pageSize,
  totalItemCount,
  filtersComponent,
  emptyStateComponent,
}: DesktopTableProps) {
  const columns = useMemo(() => {
    const templateColumns: ColumnDef<Lead>[] = [
      {
        id: "account",
        cell: ({ row: { original } }) => {
          const { id, picture_url, favorite, full_name, notes } = original;
          return (
            <div className="flex gap-x-2.5 items-center flex-1 overflow-hidden">
              {isLoading ? (
                <Skeleton className="rounded-full size-8" />
              ) : (
                <ProfileImage src={picture_url} size="sm" />
              )}

              {isLoading ? (
                <Skeleton className="w-28 h-4" />
              ) : (
                <>
                  <span
                    className="text-start text-xs-bold text-nowrap truncate flex-1"
                    title={full_name}
                  >
                    {full_name}
                  </span>
                  <div className="flex gap-x-2.5 ml-1 items-center">
                    <LeadNotesIndicator
                      noteCount={notes.length}
                      onClick={() => openSidebar(id, "notes")}
                    />
                    <FavoriteLead leadId={id} isFavorite={favorite} />
                  </div>
                </>
              )}
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "occupation",
        header: "Occupation",
        cell: ({
          row: {
            original: { id },
          },
        }) => {
          return (
            <LeadDetailTag
              leadKey="occupation"
              leadId={id}
              isLoading={isLoading}
            />
          );
        },
        meta: { icon: IdentificationCard, className: "min-w-40 max-w-40" },
      },
      {
        id: "accounts",
        header: "Accounts",
        cell: ({
          row: {
            original: { lead_of },
          },
        }) => {
          return isLoading ? (
            <Skeleton className="h-5 rounded-full w-20" />
          ) : (
            <LinkedInAccountsTag accountIds={lead_of} size="md" />
          );
        },
        meta: { icon: UserList, className: "min-w-40 max-w-40" },
      },
      {
        id: "origins",
        header: "Origins",
        cell: ({
          row: {
            original: { id },
          },
        }) => <LeadSource leadId={id} variant="plain" />,
        meta: { icon: FunnelSimple, className: "min-w-40 max-w-40" },
      },
      {
        id: "currentCompanies",
        header: "Current company",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag
            leadKey="current_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: Briefcase, className: "min-w-40 max-w-40" },
      },
      {
        id: "currentCompaniesTime",
        header: "Current company time",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadCompanyTimeTag
            leadKey="current_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: Briefcase, className: "min-w-40 max-w-40" },
      },
      {
        id: "pastCompanies",
        header: "Past company",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag
            leadKey="work_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: ClockCounterClockwise, className: "min-w-40 max-w-40" },
      },
      {
        id: "pastCompaniesTime",
        header: "Past company time",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadCompanyTimeTag
            leadKey="work_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: ClockCounterClockwise, className: "min-w-40 max-w-40" },
      },
      {
        id: "location",
        header: "Location",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag leadKey="location" leadId={id} isLoading={isLoading} />
        ),
        meta: { icon: MapPin, className: "min-w-40 max-w-40" },
      },
      {
        id: "education",
        header: "Education",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag
            leadKey="education"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: GraduationCap, className: "min-w-40 max-w-40" },
      },
      {
        id: "skills",
        header: "Skills",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag leadKey="skills" leadId={id} isLoading={isLoading} />
        ),
        meta: { icon: BookOpenText, className: "min-w-40 max-w-40" },
      },
      {
        id: "interests",
        header: "Interests",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag
            leadKey="interests"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: ListStar, className: "min-w-40 max-w-40" },
      },
      {
        id: "languages",
        header: "Languages",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag
            leadKey="languages"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: Translate, className: "min-w-40 max-w-40" },
      },
      {
        id: "labels",
        header: "Labels",
        cell: ({
          row: {
            original: { id, labels },
          },
        }) =>
          isLoading ? (
            <Skeleton className="h-6 w-20" />
          ) : (
            <DesktopLabels labels={labels} profileId={id} />
          ),
        meta: {
          icon: TagIcon,
          className: "min-w-56 max-w-56",
        },
      },
    ];

    return templateColumns;
  }, [isLoading]);

  const table = useDataTable(
    {
      data: leads || new Array(pageSize).fill({}),
      columns,
    },
    {
      localStorageId: "leads",
      onRowSelectionChange: setSelectedLeads,
      selectedRows: selectedLeads,
    },
  );

  return (
    <>
      <div className="flex flex-row items-center gap-x-1.5">
        {filtersComponent}
        <ColumnsVisibilityFilter table={table} />
      </div>

      {leads && !leads.length ? (
        emptyStateComponent
      ) : (
        <DataTable
          title="Leads"
          table={table}
          onRowClick={({ id }) => openSidebar(id, "preview")}
          totalItemCount={totalItemCount}
          isLoading={isLoading}
          selectionActionsComponent={
            <BulkManageDesktop
              actions={MANAGE_LEADS_ACTIONS}
              selection={selectedLeads}
              renderActionComponent={({ action, onClose }) => {
                const commonProps = {
                  selectedLeads,
                  onBack: onClose,
                  onSuccess: onClose,
                };
                if (action === "labels") {
                  return <UpdateLabels {...commonProps} />;
                }
                if (action === "notes") {
                  return <AddNotes {...commonProps} />;
                }
                return <FavoriteLeads action={action} {...commonProps} />;
              }}
            />
          }
        />
      )}
    </>
  );
}
